import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Splits an array into consecutive chunks of at most `size` items.
 * Used to bound request concurrency/batch size (e.g. bounded-concurrency
 * `Promise.all` pools, or staying under a server-side array size limit).
 */
export function chunkArray<T>(items: T[], size: number): T[][] {
  if (size <= 0 || items.length === 0) return items.length ? [items] : [];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * A currency amount for display, at its REAL precision — never blindly
 * rounded to 2 decimals.
 *
 * Live incident, 2026-09-20 (issue report #15): a vendor price-list entry of
 * 0.125 JOD (fils are this currency's 3rd decimal, ordinary data here, not
 * an edge case — see the item's own `step={0.001}` price inputs) displayed
 * as "0.13" on THREE separate screens that all rounded to 2 decimals: the
 * Price Lists admin table (`toLocaleString(..., {maximumFractionDigits:2})`),
 * the PO "Compare vendor prices" popover, and the PO price-override toast.
 * Every one of them appeared to CONFIRM a buyer's manually-typed 0.13 was
 * correct, while the actual stored/enforced price stayed 0.125 — the buyer
 * had no way to know the two numbers differed until the server's 400 (which
 * prints the raw, unrounded value) rejected the purchase order.
 *
 * Shows up to 4 decimals (this system's real currency precision — a
 * converted price is stored to 4, see `PRICE_DECIMALS` server-side) but
 * never fewer than 2, so an ordinary 2-decimal amount ("5.5") still reads as
 * currency ("5.50") exactly as before this existed.
 */
export function formatPricePrecise(price: number): string {
  const rounded = Math.round(price * 10_000) / 10_000;
  const trimmed = rounded.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  const decimals = trimmed.includes('.') ? trimmed.split('.')[1].length : 0;
  return rounded.toFixed(Math.max(2, decimals));
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`;
}
